// ./ConsentDetails/ClausesOrderPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
  Checkbox,
  TextField,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import AddIcon from "@mui/icons-material/Add";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import UnfoldMoreDoubleIcon from "@mui/icons-material/UnfoldMoreDouble";
import UnfoldLessDoubleIcon from "@mui/icons-material/UnfoldLessDouble";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";

// Gallery
import ImageGalleryV2 from "../ImageGallery/imageGalleryV2";

declare global {
  interface Window {
    CKEDITOR?: any;
  }
}

const loadCKEditorScript = () =>
  new Promise<void>((resolve) => {
    if (window.CKEDITOR) return resolve();
    const script = document.createElement("script");
    script.src = "/ckeditor/ckeditor.js";
    script.onload = () => resolve();
    document.body.appendChild(script);
  });

type Clause = {
  id: number;
  name: string;
  body: string;
  type?: string;
  order: number;
  template_id?: string | number;
};

type ClausesEditorPageProps = {
  consentBundleId: string | number;
};

let clauseIdCounter = 1000;

/* ---------------------------------- */
/* Editable Clause Card               */
/* ---------------------------------- */

type EditableCardProps = {
  consentBundleId: string | number;
  clause: Clause;
  isExpanded: boolean;
  setExpandedForClause: (id: number, expanded: boolean) => void;
  isEditingGlobal: boolean;
  setEditingGlobal: (v: boolean) => void;
  onUpdate: (id: number, newBody: string) => void;
  onRequestDelete: (c: Clause) => void;
  disableDrag?: boolean;
  dragHandleProps?: any;
};

const EditableCard: React.FC<EditableCardProps> = ({
  consentBundleId,
  clause,
  isExpanded,
  setExpandedForClause,
  isEditingGlobal,
  setEditingGlobal,
  onUpdate,
  onRequestDelete,
  disableDrag = false,
  dragHandleProps,
}) => {
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const instanceRef = useRef<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(clause.body);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (!isEditingGlobal && isEditing) handleCloseEditor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingGlobal]);

  useEffect(() => {
    return () => {
      if (instanceRef.current) instanceRef.current.destroy(true);
    };
  }, []);

  const initializeEditor = async () => {
    await loadCKEditorScript();
    if (editorRef.current && window.CKEDITOR) {
      instanceRef.current = window.CKEDITOR.replace(editorRef.current, {
        height: 300,
         width: '100%',
      });
      instanceRef.current.setData(content);
      instanceRef.current.on("change", () => {
        const data = instanceRef.current.getData();
        setContent(data);
        setHasUnsavedChanges(true);
      });
    }
  };

  const handleEditClick = async () => {
    setIsEditing(true);
    setEditingGlobal(true);
    setExpandedForClause(clause.id, true);
    setTimeout(() => initializeEditor(), 80);
  };

  const handleCloseEditor = () => {
    if (hasUnsavedChanges) {
      handleSave().finally(() => {
        instanceRef.current?.destroy(true);
        instanceRef.current = null;
        setIsEditing(false);
        setEditingGlobal(false);
      });
      return;
    }
    instanceRef.current?.destroy(true);
    instanceRef.current = null;
    setIsEditing(false);
    setEditingGlobal(false);
  };

  const handleSave = async () => {
    try {
      await axios.put(
        `/consent_bundle/${consentBundleId}/update_clause_block/${clause.order}`,
        { body: content },
        { headers: { "Content-Type": "application/json" } }
      );
      setHasUnsavedChanges(false);
      onUpdate(clause.id, content);
      toast.success("Clause saved successfully!");
    } catch (err) {
      console.error("Failed to save clause:", err);
      toast.error("Failed to save clause. Please try again.");
    }
  };

  return (
    <Card
      elevation={2}
      sx={{
        mb: 1.5,
        borderRadius: 3,
        border: "none", // no border
        boxShadow: "0 6px 18px rgba(0,0,0,0.05)", // soft depth
        overflow: "hidden",
      }}
    >
      <CardHeader
        sx={{ py: 1.25, pr: 1.5 }}
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              size="small"
              onClick={() => setExpandedForClause(clause.id, !isExpanded)}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography fontWeight={700} fontSize={16}>
              {clause.name}
            </Typography>
          </Stack>
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {!disableDrag && (
              <Tooltip title="Drag to reorder">
                <span {...(dragHandleProps || {})}>
                  <IconButton size="small">
                    <SwapVertIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!isEditing && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={handleEditClick}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {isEditing && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  onClick={handleSave}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Save
                </Button>
                <Tooltip title="Close editor">
                  <IconButton size="small" onClick={handleCloseEditor}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Delete clause">
              <IconButton
                size="small"
                color="error"
                onClick={() => onRequestDelete(clause)}
              >
                <DeleteForeverIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />
      {isExpanded && (
        <>
          <Divider />
          <CardContent sx={{ pt: 2 }}>
            {isEditing ? (
              <TextField
                inputRef={editorRef}
                id={`editor-${clause.id}`}
                fullWidth
                multiline
                minRows={8}
                placeholder="Edit clause content..."
              />
            ) : (
              <Box
                sx={{
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: "text.primary",
                  "& p": { my: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: content }}
              />
            )}
          </CardContent>
        </>
      )}
    </Card>
  );
};

/* ---------------------------------- */
/* Clauses Picker (Popover)           */
/* ---------------------------------- */

type ClausePickerProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  items: Clause[];
  onConfirm: (picked: Clause[]) => void;
};

const ClausePicker: React.FC<ClausePickerProps> = ({
  anchorEl,
  open,
  onClose,
  items,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) setSelected(new Set());
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) => c.name.toLowerCase().includes(q));
  }, [items, query]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ sx: { width: 360, borderRadius: 2 } }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon fontSize="small" />
          <TextField
            variant="standard"
            fullWidth
            placeholder="Search consent templates..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Stack>
      </Box>
      <Divider />
      <List sx={{ maxHeight: 320, overflowY: "auto", py: 0 }}>
        {filtered.map((item) => {
          const checked = selected.has(item.id);
          return (
            <ListItem
              key={item.id}
              disablePadding
              secondaryAction={
                <Checkbox
                  edge="end"
                  checked={checked}
                  onChange={() => {
                    const next = new Set(selected);
                    checked ? next.delete(item.id) : next.add(item.id);
                    setSelected(next);
                  }}
                />
              }
            >
              <ListItemButton
                dense
                onClick={() => {
                  const next = new Set(selected);
                  checked ? next.delete(item.id) : next.add(item.id);
                  setSelected(next);
                }}
              >
                <ListItemIcon>
                  <PlaylistAddIcon />
                </ListItemIcon>
                <ListItemText primary={item.name} />
              </ListItemButton>
            </ListItem>
          );
        })}
        {filtered.length === 0 && (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">No templates found.</Typography>
          </Box>
        )}
      </List>
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<LibraryAddIcon />}
          disabled={selected.size === 0}
          onClick={() => {
            const picked = items.filter((i) => selected.has(i.id));
            onConfirm(picked);
            onClose();
          }}
          sx={{ textTransform: "none" }}
        >
          Add Selected ({selected.size})
        </Button>
      </Box>
    </Popover>
  );
};

/* ---------------------------------- */
/* Main Page                          */
/* ---------------------------------- */

const ClausesEditorPage: React.FC<ClausesEditorPageProps> = ({
  consentBundleId,
}) => {
  const [loading, setLoading] = useState(true);
  const [clauses, setClauses] = useState<Clause[]>([]);
  const [expandedMap, setExpandedMap] = useState<Record<number, boolean>>({});
  const [isEditingGlobal, setIsEditingGlobal] = useState(false);

  const [availableConsentClauses, setAvailableConsentClauses] = useState<
    Clause[]
  >([]);

  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [insertIndex, setInsertIndex] = useState<number | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState<Clause | null>(null);

  const [selectedTemplateIds, setSelectedTemplateIds] = useState<
    Set<number | string>
  >(new Set());

  const [newlyAddedTemplateIds, setNewlyAddedTemplateIds] = useState<
    Set<number | string>
  >(new Set());
  const newListRef = useRef(new Set<number | string>());
  useEffect(() => {
    newListRef.current = newlyAddedTemplateIds;
  }, [newlyAddedTemplateIds]);

  // gallery state
  const [showGallery, setShowGallery] = useState(false);

  // local PDF preview state
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  /* -------- Fetch: bundle & clauses -------- */
  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`/consent_bundle/${consentBundleId}`);

        let clauseBlocksRaw = data?.consent_form?.clause_blocks;
        let clauseBlocks: any[] = [];

        if (typeof clauseBlocksRaw === "string") {
          try {
            clauseBlocks = JSON.parse(clauseBlocksRaw);
          } catch (err) {
            console.error("Failed to parse clause_blocks JSON:", err);
            toast.error("Failed to load clauses (invalid data).");
            setLoading(false);
            return;
          }
        } else if (Array.isArray(clauseBlocksRaw)) {
          clauseBlocks = clauseBlocksRaw;
        } else {
          clauseBlocks = [];
        }

        let maxId = 0;
        clauseBlocks.forEach((b: any) => {
          if (typeof b.template_id === "number" && b.template_id > maxId) {
            maxId = b.template_id;
          }
        });
        clauseIdCounter = Math.max(1000, maxId);

        const allClauses: Clause[] = clauseBlocks.map(
          (block: any, index: number) => ({
            id:
              typeof block.template_id === "number"
                ? block.template_id
                : ++clauseIdCounter,
            name: block.type
              ? block.type.charAt(0).toUpperCase() + block.type.slice(1)
              : `Clause ${index + 1}`,
            body: block.content,
            type: block.type,
            order: index + 1,
            template_id: block.template_id ?? block.id,
          })
        );

        const expanded: Record<number, boolean> = {};
        allClauses.forEach((c) => (expanded[c.id] = true));

        const templateIds = allClauses
          .filter((c) => c.type !== "intro" && c.type !== "outro")
          .map((c) => c.template_id as number | string);

        setClauses(allClauses);
        setExpandedMap(expanded);
        setSelectedTemplateIds(new Set(templateIds));
      } catch (e) {
        console.error(e);
        toast.error("Failed to load consent bundle.");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [consentBundleId]);

  /* -------- Fetch: available templates (picker) -------- */
  const loadAvailableTemplates = async () => {
    try {
      const { data } = await axios.get(
        `/consent_bundle/${consentBundleId}/get_available_consent_clauses`
      );
      setAvailableConsentClauses(data || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load consent templates.");
    }
  };

  /* -------- UI helpers -------- */
  const allOpen = useMemo(
    () => Object.values(expandedMap).every(Boolean),
    [expandedMap]
  );

  const toggleAll = () => {
    const next = !allOpen;
    const map: Record<number, boolean> = {};
    clauses.forEach((c) => (map[c.id] = next));
    setExpandedMap(map);
  };

  const setExpandedForClause = (id: number, expanded: boolean) => {
    setExpandedMap((prev) => ({ ...prev, [id]: expanded }));
  };

  /* -------- Save selected templates to server -------- */
  const persistSelectedTemplates = async (
    updatedClauses?: Clause[]
  ): Promise<void> => {
    let merged = new Set<number | string>(selectedTemplateIds);

    if (updatedClauses && updatedClauses.length > 0) {
      for (const c of updatedClauses) {
        if (c.type === "intro" || c.type === "outro") continue;
        const tid = (c.template_id ?? c.id) as number | string;
        merged.add(tid);
      }
    }

    try {
      const block_ids = Array.from(merged).map(String);
      await axios.put(
        `/consent_bundle/${consentBundleId}/update_consent_form_with_selected_templates`,
        { block_ids },
        { headers: { "Content-Type": "application/json" } }
      );
      setSelectedTemplateIds(merged);
      toast.success("Templates updated.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update selected templates.");
    }
  };

  /* -------- Add / Delete / Reorder -------- */
  const handleAddAt = async (index: number, picked: Clause[]) => {
    if (!picked || picked.length === 0) return;

    let newClauses = [...clauses];
    let position = index;

    picked.forEach((p) => {
      const generatedId = ++clauseIdCounter;
      const newClause: Clause = {
        id: generatedId,
        template_id: p.template_id ?? p.id,
        name: p.name || `New Clause ${generatedId}`,
        body: p.body || "<p>New content here...</p>",
        order: position + 1,
      };
      newClauses.splice(position, 0, newClause);
      position += 1;
    });

    newClauses = newClauses.map((c, idx) => ({ ...c, order: idx + 1 }));
    setClauses(newClauses);

    const extra = new Set(newListRef.current);
    picked.forEach((p) => extra.add(p.template_id ?? p.id));
    setNewlyAddedTemplateIds(extra);

    await persistSelectedTemplates(
      newClauses.filter((c) => extra.has(c.template_id as any))
    );
  };

  const handleDelete = async (c: Clause) => {
    try {
      const orderNumber = c.order;

      const nextSelected = new Set(
        [...selectedTemplateIds].filter((tid) => tid !== c.template_id)
      );

      await axios.delete(
        `/consent_bundle/${consentBundleId}/delete_clause_block/${orderNumber}`,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          data: {
            block_ids: Array.from(nextSelected),
          },
        }
      );

      setClauses((prev) => prev.filter((x) => x.id !== c.id));
      setSelectedTemplateIds(nextSelected);
      setDeleteConfirm(null);
      toast.success("Clause deleted.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete clause.");
    }
  };

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (sourceIndex === destIndex) return;

    const next = [...clauses];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(destIndex, 0, moved);

    const withOrders = next.map((c, idx) => ({ ...c, order: idx + 1 }));
    setClauses(withOrders);

    try {
      await axios.put(
        `/consent_bundle/${consentBundleId}/move_position_of_clause_block/${moved.order}`,
        { new_position: destIndex + 1 },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
    } catch (e) {
      console.error("Failed to reorder:", e);
      toast.error("Failed to reorder clauses.");
    }
  };

  const handleUpdateBody = (id: number, newBody: string) => {
    setClauses((prev) =>
      prev.map((c) => (c.id === id ? { ...c, body: newBody } : c))
    );
  };

  /* -------- Picker handlers -------- */
  const openPickerAt = async (el: HTMLElement, index: number) => {
    setInsertIndex(index);
    setPickerAnchor(el);
    if (availableConsentClauses.length === 0) {
      await loadAvailableTemplates();
    }
  };

  const closePicker = () => {
    setPickerAnchor(null);
    setInsertIndex(null);
  };

  /* -------- PDF Preview (local + server) -------- */
  const uploadCallback = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post(`/consent_clause/upload_image_to_s3`, formData);
    return { data: { link: res.data } };
  };

  const buildConsentHtml = (): string => {
    // Order-safe join of clause HTML
    const ordered = [...clauses].sort((a, b) => a.order - b.order);
    const html = ordered.map((c) => c.body || "").join("\n");
    return html || "";
  };

  const previewPdf = async () => {
    try {
      setIsGeneratingPdf(true);

      // 1) Combine current clause bodies
      let consentForm = buildConsentHtml();

      if (!consentForm.trim()) {
        // final safety: fetch fresh from server
        const { data } = await axios.get(`/consent_bundle/${consentBundleId}`);
        consentForm = data?.consent_form?.body || "";
      }
      if (!consentForm.trim()) {
        toast.error("There is no content to preview.");
        return;
      }

      // 2) Normalize images (size + data-URL upload)
      const parser = new DOMParser();
      const doc = parser.parseFromString(consentForm, "text/html");
      const imgTags = Array.from(doc.querySelectorAll("img"));
      let updatedConsentForm = doc.body.innerHTML;

      for (const img of imgTags) {
        const src = img.getAttribute("src") || "";
        const imgAlt = img.getAttribute("alt") || "";
        const imgHeight = img.getAttribute("height");
        const imgWidth = img.getAttribute("width");

        if (
          !img.getAttribute("style") ||
          !/height|width/.test(img.getAttribute("style") || "")
        ) {
          const imgStyle = `height: ${imgHeight || "auto"}px; width: ${
            imgWidth || "auto"
          }px;`;
          const correctedImgTag = `<img src="${src}" alt="${imgAlt}" style="${imgStyle}" />`;
          updatedConsentForm = updatedConsentForm.replace(
            img.outerHTML,
            correctedImgTag
          );
        }

        if (src.startsWith("data:image")) {
          const mimeTypeMatch = src.match(/data:(image\/[a-zA-Z]+);base64,/);
          if (mimeTypeMatch) {
            const mimeType = mimeTypeMatch[1];
            const base64Data = src.replace(/data:image\/[a-zA-Z]+;base64,/, "");
            const byteString = atob(base64Data);
            const arrayBuffer = new Uint8Array(byteString.length);
            for (let i = 0; i < byteString.length; i++)
              arrayBuffer[i] = byteString.charCodeAt(i);

            const file = new File([arrayBuffer], "image.png", { type: mimeType });
            const uploadRes = await uploadCallback(file);

            const newImgTag = `<img src="${uploadRes.data.link}" alt="${imgAlt}" style="height: 300px; width: 300px;" />`;
            updatedConsentForm = updatedConsentForm.replace(
              img.outerHTML,
              newImgTag
            );
          }
        }
      }

      // 3) Generate PDF via backend
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/generate_preview_pdf`,
        method: "POST",
        responseType: "blob",
        data: { consentForm: updatedConsentForm },
      });

      // 4) Open in new tab
      const href = URL.createObjectURL(response.data);
      window.open(href, "_blank", "noopener,noreferrer");
      // URL.revokeObjectURL can be called later if you store href somewhere
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate preview PDF.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  /* ---------------------------------- */
  /* Render                             */
  /* ---------------------------------- */

  if (loading) {
    return (
      <Box
        sx={{
          py: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading clauses…
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={800}>
          {/* spacer */}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          {/* Collapse/Expand all */}
          <Tooltip title={allOpen ? "Collapse all" : "Expand all"}>
            <Button
              variant="contained"
              size="small"
              startIcon={
                allOpen ? <UnfoldLessDoubleIcon /> : <UnfoldMoreDoubleIcon />
              }
              onClick={toggleAll}
              sx={{
                borderRadius: "12px",
                px: 1.5,
                py: 0.25, // reduced height
                fontSize: "0.875rem",
                bgcolor: allOpen ? "#374151" : "#ff9500",
                color: allOpen ? "white" : "black",
                textTransform: "none",
                "&:hover": {
                  bgcolor: allOpen ? "#1f2937" : "#e68600",
                },
              }}
            >
              {allOpen ? "Collapse all" : "Expand all"}
            </Button>
          </Tooltip>

          {/* Open/Close Gallery */}
          <Tooltip title={showGallery ? "Close gallery" : "Open gallery"}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setShowGallery((s) => !s)}
              sx={{
                borderRadius: "12px",
                px: 1.5,
                py: 0.25, // reduced height
                fontSize: "0.875rem",
                bgcolor: showGallery ? "#374151" : "#ff9500",
                color: showGallery ? "white" : "black",
                textTransform: "none",
                "&:hover": {
                  bgcolor: showGallery ? "#1f2937" : "#e68600",
                },
              }}
            >
              {showGallery ? "Close gallery" : "Open gallery"}
            </Button>
          </Tooltip>

          {/* Add from template library (append to end) */}
          <Tooltip title="Add clause from template library (at end)">
            <span>
              <Button
                variant="contained"
                size="small"
                color="warning"
                startIcon={<LibraryAddIcon />}
                onClick={async (e) => {
                  await openPickerAt(
                    e.currentTarget as HTMLElement,
                    clauses.length
                  );
                }}
                sx={{
                  borderRadius: "12px",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.875rem",
                  bgcolor: pickerAnchor ? "#374151" : "#ff9500",
                  color: pickerAnchor ? "white" : "black",
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: pickerAnchor ? "#1f2937" : "#e68600",
                  },
                }}
              >
                Add from Consent Template Library
              </Button>
            </span>
          </Tooltip>

          {/* Preview PDF */}
          <Button
            variant="contained"
            size="small"
            disabled={isGeneratingPdf}
            onClick={previewPdf}
            sx={{
              borderRadius: "12px",
              px: 1.5,
              py: 0.5,
              fontSize: "0.875rem",
              bgcolor: "#ff9500",
              color: "black",
              textTransform: "none",
              "&:hover": { bgcolor: "#e68600" },
            }}
            startIcon={isGeneratingPdf ? <CircularProgress size={16} /> : undefined}
          >
            {isGeneratingPdf ? "Preparing…" : "Preview PDF"}
          </Button>

          {/* Refresh templates */}
          <Tooltip title="Refresh available templates">
            <IconButton onClick={loadAvailableTemplates}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Main content: Clauses list + optional Gallery */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems="stretch"
      >
        {/* Left: clauses column */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {clauses.length === 0 && (
            <Card
              elevation={0}
              sx={{ borderRadius: 3, py: 6, textAlign: "center", border: "none" }}
            >
              <Typography variant="body1" sx={{ mb: 1 }}>
                No clauses yet.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use the library to insert clauses into this consent form.
              </Typography>
              <Button
                variant="contained"
                color="warning"
                startIcon={<LibraryAddIcon />}
                onClick={async (e) => {
                  await openPickerAt(e.currentTarget as HTMLElement, 0);
                }}
                sx={{ textTransform: "none" }}
              >
                Add from Library
              </Button>
            </Card>
          )}

          {clauses.length > 0 && (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}>
                    {clauses.map((clause, index) => {
                      const isIntro = clause.type === "intro";
                      const isOutro = clause.type === "outro";

                      const card = (
                        <EditableCard
                          consentBundleId={consentBundleId}
                          key={clause.id}
                          clause={clause}
                          isExpanded={!!expandedMap[clause.id]}
                          setExpandedForClause={setExpandedForClause}
                          isEditingGlobal={isEditingGlobal}
                          setEditingGlobal={setIsEditingGlobal}
                          onUpdate={handleUpdateBody}
                          onRequestDelete={(c) => setDeleteConfirm(c)}
                          disableDrag={isIntro || isOutro}
                        />
                      );

                      // Subtle "+" add-between icon (no text, gray, no border)
                      const AddBetweenIcon = (
                        <IconButton
                          size="small"
                          onClick={async (e) =>
                            openPickerAt(e.currentTarget as HTMLElement, index + 1)
                          }
                          sx={{
                            bgcolor: "grey.100",
                            color: "text.secondary",
                            "&:hover": { bgcolor: "grey.200" },
                            boxShadow: "none",
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      );

                      if (isIntro || isOutro) {
                        return (
                          <React.Fragment key={clause.id}>
                            {card}
                            {isIntro && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  mb: 2,
                                }}
                              >
                                {AddBetweenIcon}
                              </Box>
                            )}
                          </React.Fragment>
                        );
                      }

                      return (
                        <Draggable
                          key={clause.id}
                          draggableId={clause.id.toString()}
                          index={index}
                        >
                          {(dragProvided, snapshot) => (
                            <Box
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              sx={{
                                transition: "box-shadow .2s",
                                ...(snapshot.isDragging
                                  ? {
                                      boxShadow: "0 8px 24px rgba(0,0,0,.12)",
                                      borderRadius: 3,
                                    }
                                  : {}),
                              }}
                            >
                              <EditableCard
                                consentBundleId={consentBundleId}
                                clause={clause}
                                isExpanded={!!expandedMap[clause.id]}
                                setExpandedForClause={setExpandedForClause}
                                isEditingGlobal={isEditingGlobal}
                                setEditingGlobal={setIsEditingGlobal}
                                onUpdate={handleUpdateBody}
                                onRequestDelete={(c) => setDeleteConfirm(c)}
                                dragHandleProps={dragProvided.dragHandleProps}
                              />

                              {/* icon-only add-between */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  mb: 2,
                                }}
                              >
                                {AddBetweenIcon}
                              </Box>
                            </Box>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Box>

        {/* Right: gallery column */}
        {showGallery && (
          <Box
            sx={{
              width: { xs: "100%", md: 420 },
              maxWidth: { xs: "100%", md: 420 },
              minWidth: { xs: "100%", md: 360 },
              height: 675,
              borderRadius: 3,
              border: "1px solid rgba(255, 149, 0, 0.3)",
              boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
              bgcolor: "background.paper",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                }}
              >
                <ImageGalleryV2
                  showGallery={showGallery}
                  onClose={() => setShowGallery(false)}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Stack>

      {/* Clause picker */}
      <ClausePicker
        anchorEl={pickerAnchor}
        open={Boolean(pickerAnchor)}
        onClose={closePicker}
        items={availableConsentClauses}
        onConfirm={(picked) => {
          if (insertIndex === null) return;
          handleAddAt(insertIndex, picked);
        }}
      />

      {/* Delete confirm */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirm deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete clause{" "}
            <strong>{deleteConfirm?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClausesEditorPage;
